import { Component, ChangeDetectorRef, OnInit, Input, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';

interface QueryGroup {
  operator: 'AND' | 'OR';
  clauses: Array<QueryGroup>;
}

interface QueryCondition {
  logicalOperator: 'AND' | 'OR';
  field: string;
  operator: string;
  value: any;
  valueStart?: string;
  valueEnd?: string;
  valueList?: any[];
}

interface InitialQueryData {
  groups: Array<{
    logicalOperator?: 'AND' | 'OR';
    conditions: Array<{
      logic?: 'AND' | 'OR';
      field: string;
      operator: string;
      value: any;
    }>;
  }>;
}

@Component({
  selector: 'app-view-query-builder',
  templateUrl: './view-query-builder.component.html',
  styleUrl: './view-query-builder.component.scss',
})
export class ViewQueryBuilderComponent implements OnInit {
  queryForm: FormGroup;
  generatedQuery: any;
  generatedSQL: string = '';

  @Input() fields: { label: string; value: string }[] = [];
  @Input() initialQueryJson: any;

  operatorGroups = [
    {
      category: 'Comparison',
      operators: [
        { label: 'Equals (=)', value: '=' },
        { label: 'Not Equal (≠)', value: '!=' },
        { label: 'Less Than (<)', value: '<' },
        { label: 'Greater Than (>)', value: '>' },
        { label: 'Less Than or Equal (≤)', value: '<=' },
        { label: 'Greater Than or Equal (≥)', value: '>=' },
      ],
    },

    {
      category: 'Special',
      operators: [
        { label: 'LIKE', value: 'LIKE' },
        { label: 'NOT LIKE', value: 'NOT LIKE' },
        { label: 'IN', value: 'IN' },
        { label: 'NOT IN', value: 'NOT IN' },
        { label: 'BETWEEN', value: 'BETWEEN' },
        { label: 'NOT BETWEEN', value: 'NOT BETWEEN' },
        { label: 'IS NULL', value: 'IS NULL' },
        { label: 'IS NOT NULL', value: 'IS NOT NULL' },
        // { label: 'EXISTS', value: 'EXISTS' },
        // { label: 'NOT EXISTS', value: 'NOT EXISTS' },
        // { label: 'ANY', value: 'ANY' },
        // { label: 'ALL', value: 'ALL' },
        // { label: 'SOME', value: 'SOME' },
      ],
    },
    {
      category: 'Advanced',
      operators: [
        { label: 'REGEXP', value: 'REGEXP' },
        // { label: 'SIMILAR TO', value: 'SIMILAR_TO' },
        // { label: 'IS DISTINCT FROM', value: 'IS_DISTINCT_FROM' },
        // { label: 'OVERLAPS', value: 'OVERLAPS' },
      ],
    },
  ];

  constructor(
    private fb: FormBuilder,
    private cdref: ChangeDetectorRef,
  ) {
    this.queryForm = this.fb.group({
      groups: this.fb.array([this.createGroup()]),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialQueryJson']) {
      if (changes['initialQueryJson'].currentValue) {
        console.log(changes['initialQueryJson'].currentValue);
        this.setInitialValuesFromOutput(this.initialQueryJson);
        this.generateSQL();
      }
    }
  }

  ngOnInit() {}

  get groups() {
    return this.queryForm.get('groups') as FormArray;
  }

  createGroup(): FormGroup {
    return this.fb.group({
      logicalOperator: ['AND'],
      conditions: this.fb.array([this.createFirstCondition()]),
    });
  }

  private createBaseCondition(): FormGroup {
    return this.fb.group(
      {
        logicalOperator: ['AND', Validators.required],
        field: ['', Validators.required],
        operator: ['', Validators.required],
        value: [''],
        valueStart: [''],
        valueEnd: [''],
        valueList: this.fb.control([]),
      },
      { validators: this.conditionValidator },
    );
  }

  private conditionValidator: ValidatorFn = (control: AbstractControl) => {
    const group = control as FormGroup;
    const operator = group.get('operator')?.value;
    const value = group.get('value')?.value;
    const valueStart = group.get('valueStart')?.value;
    const valueEnd = group.get('valueEnd')?.value;
    const valueList = group.get('valueList')?.value;

    if (this.isValueDisabled(operator)) return null;

    const errors: any = {};

    switch (operator) {
      case 'BETWEEN':
      case 'NOT BETWEEN':
        if (!valueStart || !valueEnd) errors.invalidBetween = true;
        break;
      case 'IN':
      case 'NOT IN':
        if (!valueList?.length) errors.invalidIn = true;
        break;
      case 'REGEXP':
        if (!this.validateRegex(value)) errors.invalidRegex = true;
        break;
      default:
        if (!value) errors.required = true;
    }

    return Object.keys(errors).length ? errors : null;
  };

  getGroupConditions(groupIndex: number): FormArray {
    const group = this.groups.at(groupIndex) as FormGroup;
    return group.get('conditions') as FormArray;
  }

  addGroup() {
    this.groups.push(this.createGroup());
  }

  addConditionToGroup(groupIndex: number) {
    const conditions = this.getGroupConditions(groupIndex);
    const newCondition =
      conditions.length === 0 ? this.createFirstCondition() : this.createCondition();

    conditions.push(newCondition);
  }

  removeCondition(groupIndex: number, conditionIndex: number) {
    const conditions = this.getGroupConditions(groupIndex);
    if (conditions.length > 1) {
      conditions.removeAt(conditionIndex);

      if (conditions.length > 0) {
        const firstCondition = conditions.at(0) as FormGroup;
        if (firstCondition.contains('logicalOperator')) {
          firstCondition.get('logicalOperator')?.disable();
        }
      }
    }
  }

  removeGroup(index: number) {
    if (this.groups.length > 1) {
      this.groups.removeAt(index);
    }
  }

  getLogicalOperatorControl(groupIndex: number, conditionIndex: number): FormControl {
    const condition = this.getGroupConditions(groupIndex).at(conditionIndex) as FormGroup;
    return condition.get('logicalOperator') as FormControl;
  }

  createCondition(): FormGroup {
    return this.createBaseCondition();
  }

  getValuePlaceholder(operator: string): string {
    switch (operator) {
      case 'BETWEEN':
        return 'Start value';
      case 'IN':
      case 'NOT IN':
        return 'Select values';
      case 'REGEXP':
        return 'Regular expression';
      case 'SIMILAR_TO':
        return 'Pattern';
      default:
        return 'Value';
    }
  }

  getValueHint(operator: string): string {
    switch (operator) {
      case 'BETWEEN':
        return 'Enter start and end values';
      case 'NO BETWEEN':
        return 'Enter start and end values';
      case 'IN':
      case 'NOT IN':
        return 'Select multiple values';
      case 'LIKE':
        return 'Use % as wildcard';
      case 'NOT LIKE':
        return 'Use % as wildcard';
      case 'REGEXP':
        return 'Enter regular expression';
      default:
        return '';
    }
  }

  isValueDisabled(operator: string | null): boolean {
    if (!operator) return false;
    return ['IS NULL', 'IS NOT NULL', 'EXISTS', 'NOT EXISTS'].includes(operator);
  }

  private validateRegex(pattern: string): boolean {
    try {
      new RegExp(pattern);
      return true;
    } catch {
      return false;
    }
  }

  private generateSQL(): string {
    const groups = this.queryForm.value.groups;
    const whereClauses: string[] = [];

    groups.forEach((group: any, groupIndex: number) => {
      const conditionClauses: string[] = [];

      group.conditions.forEach((condition: any, condIndex: number) => {
        const clause = this.buildConditionClause(condition);

        if (condIndex > 0) {
          const logic = condition.logicalOperator || 'AND';
          conditionClauses.push(logic);
        }

        conditionClauses.push(clause);
      });

      if (conditionClauses.length > 0) {
        const groupClause = this.wrapGroup(conditionClauses);

        if (groupIndex > 0) {
          whereClauses.push(group.logicalOperator || 'AND');
        }

        whereClauses.push(groupClause);
      }
    });

    return whereClauses.length > 0 ? `${whereClauses.join(' ')}` : '';
  }

  createFirstCondition(): FormGroup {
    const group = this.createBaseCondition();
    group.get('logicalOperator')?.disable();

    group.patchValue({
      logicalOperator: 'AND',
      valueList: [],
    });
    return group;
  }

  private escapeValue(value: any): string {
    if (typeof value === 'string') {
      if (value.includes("'")) return `"${value}"`;
      if (/^[\w\s]+$/.test(value)) return `'${value}'`;
      return `'${value.replace(/'/g, "''")}'`;
    }
    return value + '';
  }

  onSubmit() {
    try {
      if (this.queryForm.valid) {
        this.markAllAsTouched();
        this.generatedQuery = this.formatOutputJSON();
        this.generatedSQL = this.generateSQL();
      } else {
        this.markAllAsTouched();
      }
    } catch (error) {
      console.error('Error generating query:', error);
      this.generatedSQL = '/* Error generating SQL query */';
    }
  }

  private markAllAsTouched() {
    this.groups.controls.forEach((group) => {
      const conditions = (group as FormGroup).get('conditions') as FormArray;
      conditions.controls.forEach((condition) => {
        condition.markAllAsTouched();
      });
    });
  }

  private formatValueForJSON(condition: any): any {
    const operator = condition.operator;
    switch (operator) {
      case 'BETWEEN':
        return { start: condition.valueStart, end: condition.valueEnd };
      case 'IN':
      case 'NOT IN':
        return condition.valueList;
      case 'OVERLAPS':
        return {
          start: condition.valueStart,
          end: condition.valueEnd,
          type: 'DATE_RANGE',
        };
      case 'EXISTS':
      case 'NOT EXISTS':
        return { subquery: condition.value };
      default:
        return condition.value;
    }
  }

  private formatOutputJSON() {
    return {
      groups: this.queryForm.value.groups.map((group: any) => ({
        logicalOperator: group.logicalOperator,
        conditions: group.conditions.map((condition: any) => ({
          logic: condition.logicalOperator,
          field: condition.field,
          operator: condition.operator,
          value: this.formatValueForJSON(condition),
        })),
      })),
    };
  }

  private buildConditionClause(condition: any): string {
    const { field, operator, logicalOperator } = condition;
    const value = this.formatSQLValue(condition);

    switch (operator) {
      case 'IS NULL':
      case 'IS NOT NULL':
        return `${field} ${operator}`;

      case 'EXISTS':
      case 'NOT EXISTS':
        return `${operator} (${value})`;

      case 'BETWEEN':
      case 'NOT BETWEEN':
        return `${field} ${operator} ${value}`;

      case 'IN':
      case 'NOT IN':
        return `${field} ${operator} ${value}`;

      case 'ANY':
      case 'ALL':
      case 'SOME':
        return `${field} = ${operator} (${value})`;

      case 'OVERLAPS':
        return `${field} ${operator} ${value}`;

      case 'IS_DISTINCT_FROM':
        return `${field} IS DISTINCT FROM ${value}`;

      default:
        return `${field} ${operator} ${value}`;
    }
  }

  private wrapGroup(conditionClauses: string[]): string {
    if (conditionClauses.length === 0) return '';

    const joined = conditionClauses.join(' ');

    return conditionClauses.length > 1 ? `(${joined})` : joined;
  }

  private formatSQLValue(condition: any): string {
    const operator = condition.operator;
    const value = condition.value;

    switch (operator) {
      case 'BETWEEN':
      case 'NOT BETWEEN':
        return `${this.escapeValue(
          condition.valueStart,
        )} AND ${this.escapeValue(condition.valueEnd)}`;

      case 'IN':
      case 'NOT IN':
        const values = condition.valueList || [];
        if (values.length === 0) return '()';
        return `(${values.map((v: string) => this.escapeValue(v)).join(', ')})`;

      case 'LIKE':
      case 'NOT LIKE':
        return `'${value}'`;

      case 'EXISTS':
      case 'NOT EXISTS':
      case 'ANY':
      case 'ALL':
      case 'SOME':
        return this.validateSubquery(value);

      case 'REGEXP':
        return this.escapeValue(value);

      case 'OVERLAPS':
        return `(DATE '${this.formatDate(
          condition.valueStart,
        )}', DATE '${this.formatDate(condition.valueEnd)}')`;

      case 'IS_DISTINCT_FROM':
        return this.escapeValue(value);

      default:
        return this.escapeValue(value);
    }
  }

  private formatDate(value: string): string {
    if (!value) return '';
    return new Date(value).toISOString().split('T')[0];
  }

  private validateSubquery(value: string): string {
    try {
      if (!value) return '/* Invalid Subquery */';

      // Trim whitespace and remove enclosing parentheses
      const trimmedValue = value
        .trim()
        .replace(/^\((.*)\)$/, '$1')
        .trim();

      // Check if the core query is valid (case-insensitive)
      if (/^\s*SELECT\s+.*/i.test(trimmedValue)) {
        // Ensure the original value is wrapped in parentheses if needed
        return value.trim().startsWith('(') ? value.trim() : `(${trimmedValue})`;
      }

      return '/* Invalid Subquery */';
    } catch {
      return '/* Invalid Subquery */';
    }
  }

  onOperatorChange(groupIndex: number, conditionIndex: number) {
    const condition = this.getGroupConditions(groupIndex).at(conditionIndex) as FormGroup;
    const operator = condition.get('operator')?.value;

    condition.patchValue({
      value: '',
      valueStart: '',
      valueEnd: '',
      valueList: [],
    });

    setTimeout(() => {
      condition.updateValueAndValidity();
    });

    const valueControl = condition.get('value');
    if (this.isValueDisabled(operator)) {
      valueControl?.disable();
    } else {
      valueControl?.enable();
    }
  }

  getValueList(groupIndex: number, conditionIndex: number): FormControl {
    const condition = this.getGroupConditions(groupIndex).at(conditionIndex) as FormGroup;
    return condition.get('valueList') as FormControl;
  }

  getValueListControl(groupIndex: number, conditionIndex: number): FormControl {
    const condition = this.getGroupConditions(groupIndex).at(conditionIndex) as FormGroup;
    return condition.get('valueList') as FormControl;
  }

  public setInitialValuesFromOutput(data: InitialQueryData): void {
    // Clear existing groups
    this.groups.clear();

    data.groups.forEach((group, groupIndex) => {
      const newGroup = this.createGroup();

      // Set group logical operator (skip for first group)
      if (groupIndex > 0) {
        newGroup.patchValue({
          logicalOperator: group.logicalOperator || 'AND',
        });
      }

      const groupConditions = newGroup.get('conditions') as FormArray;
      groupConditions.clear();

      group.conditions.forEach((condition, condIndex) => {
        const isFirstCondition = condIndex === 0;
        const formCondition = isFirstCondition
          ? this.createFirstCondition()
          : this.createCondition();

        // Map JSON value to form controls
        const formValue = this.mapOutputValueToForm(condition.operator, condition.value);

        formCondition.patchValue({
          field: condition.field,
          operator: condition.operator,
          ...formValue,
          logicalOperator: !isFirstCondition ? condition.logic || 'AND' : 'AND',
        });
        // this.onOperatorChange(groupIndex, condIndex);

        groupConditions.push(formCondition);
      });

      this.groups.push(newGroup);
    });

    this.queryForm.updateValueAndValidity();
    this.cdref.detectChanges();
    this.generatedQuery = this.formatOutputJSON();
    this.generatedSQL = this.generateSQL();
  }

  private mapOutputValueToForm(operator: string, value: any): any {
    switch (operator) {
      case 'BETWEEN':
      case 'NOT BETWEEN':
        return {
          valueStart: value?.start || '',
          valueEnd: value?.end || '',
        };

      case 'IN':
      case 'NOT IN':
        return {
          valueList: Array.isArray(value) ? value : [],
        };

      case 'IS NULL':
      case 'IS NOT NULL':
        return { value: '' };

      case 'EXISTS':
      case 'NOT EXISTS':
        return { value: value?.subquery || '' };

      case 'OVERLAPS':
        return {
          valueStart: value?.start || '',
          valueEnd: value?.end || '',
        };

      default:
        return { value };
    }
  }
}
